package app.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
sealed interface Step {
    val name: String
    val ordering: Int
    val executionCondition: ExecutionCondition
    val runCondition: RunCondition
}

/**
 * Defines how a step should behave when previous steps in the execution pipeline have failed.
 *
 * This is particularly useful for handling critical notifications or cleanup tasks that should
 * execute regardless of other steps' failures. For example:
 *
 * In a deployment pipeline with:
 * 1. ECS Deployment
 * 2. Lambda Deployment
 * 3. Slack Notification
 *
 * If Step 1 fails:
 * - Step 2 (Success) will be skipped
 * - Step 3 (AlwaysRun) will still execute to notify about the failure
 */
@Serializable
sealed interface RunCondition {
    /**
     * Indicates that the step should only execute if all previous steps were successful.
     * This is the default behavior for most deployment steps.
     */
    @Serializable
    @SerialName("SUCCESS")
    data object Success : RunCondition

    /**
     * Indicates that the step should execute regardless of previous steps' outcomes.
     * Typically used for notification, monitoring, or cleanup tasks that should run
     * even if the deployment fails.
     */
    @Serializable
    @SerialName("ALWAYS_RUN")
    data object AlwaysRun : RunCondition

    companion object {
        val entries = listOf(Success, AlwaysRun)

        fun getType(condition: RunCondition): String = when (condition) {
            is Success -> "SUCCESS"
            is AlwaysRun -> "ALWAYS_RUN"
        }
    }
}

/**
 * Defines the conditions under which a step should execute based on the deployment target
 * and execution context.
 *
 * This interface helps control step execution across different deployment environments
 * and company-specific infrastructure setups. For example:
 * - Notifications might only need to be sent once per deployment, not per company
 * - Certain steps might only be relevant for specific companies
 */
@Serializable
sealed interface ExecutionCondition {
    /**
     * Indicates that the step should execute for every company in every execution.
     * This is the default behavior for most deployment steps.
     */
    @Serializable
    @SerialName("ALWAYS")
    data object Always : ExecutionCondition

    /**
     * Indicates that the step is disabled in every execution.
     * Might helpful for debugging purpose for ex. disabling notification steps.
     */
    @Serializable
    @SerialName("DISABLED")
    data object Disabled : ExecutionCondition

    /**
     * Indicates that the step should only execute once per deployment, regardless
     * of how many companies are involved. Typically used for global notifications
     * or status updates that shouldn't be repeated for each company.
     *
     * Example: When deploying to dev stage across 3 companies, a Slack notification
     * should only be sent once, not three times.
     */
    @Serializable
    @SerialName("PER_EXECUTION")
    data object PerExecution : ExecutionCondition

    /**
     * Indicates that the step should only execute for specific companies.
     * Used when certain steps are only applicable to particular company environments.
     *
     * Example: Company A might have Lambda functions while Company B doesn't,
     * so Lambda deployment steps should only run for Company A.
     *
     * @property companyIds List of company IDs where this step should execute
     */
    @Serializable
    @SerialName("FOR_COMPANIES")
    data class ForCompanies(val companyIds: List<Int>) : ExecutionCondition

    companion object {
        val entries = listOf(Always, Disabled, PerExecution, ForCompanies(emptyList()))

        fun getType(condition: ExecutionCondition): String = when (condition) {
            is Always -> "ALWAYS"
            is Disabled -> "DISABLED"
            is PerExecution -> "PER_EXECUTION"
            is ForCompanies -> "FOR_COMPANIES"
        }
    }
}

@Serializable
data class ExecutionTarget(
    val projectName: String,
    val companyProjectStageId: Int,
    val companyId: Int,
    val companyName: String,
    val stageId: Int,
    val stageName: String,
)

interface StepExecutor<T : Step> {
    suspend fun execute(
        step: T,
        rolloutExecution: RolloutExecution,
        deployables: List<Deployable>,
        projectName: String,
        previousException: Exception?,
    ): Result<Unit>
}

@Serializable
@SerialName("is_even")
data class IsEvenStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val isEven: String,
) : Step

@Serializable
@SerialName("update_dt")
data class UpdateDtStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val projectName: String,
    val parentProjectUUID: String,
    val apiUrl: String,
    val apiKey: String,
) : Step

@Serializable
@SerialName("update_dt_sbom")
data class UpdateDtSbomStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val projectName: String,
    val apiUrl: String,
    val apiKey: String,
) : Step

@Serializable
@SerialName("upload_s3")
data class UploadS3Step(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val awsAccount: String, //placeholder for company_aws_account refactor
    val awsRegion: String,
    val deployableName: String,
    val bucketURI: String,
) : Step

@Serializable
@SerialName("invalidate_cloudfront")
data class InvalidateCloudfrontStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val awsAccount: String, //placeholder for company_aws_account refactor
    val cnameTarget: String,
    val invalidationPath: String,
    val waitForInvalidation: Boolean,
) : Step

@Serializable
@SerialName("update_lambda")
data class UpdateLambdaStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val awsRegion: String,
    val awsAccount: String,
    val functionName: String,
    val deployableName: String,
) : Step

@Serializable
@SerialName("slack_notification")
data class SlackNotificationStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val webhookUrl: String,
) : Step

@Serializable
@SerialName("ecr_deployment")
data class EcsDeploymentStep(
    override val name: String,
    override val ordering: Int,
    override val executionCondition: ExecutionCondition,
    override val runCondition: RunCondition,
    val awsRegion: String,
    val awsAccount: String,
    val clusterName: String,
    val serviceName: String,
    val taskDefName: String,
    val containerDefinitions: List<ContainerDefinition>,
) : Step

@Serializable
data class ContainerDefinition(
    val containerName: String,
    val deployableName: String,
)
